import React from "react";

import { useBreakpoint } from "~/hooks";
import { CALENDAR_PROVIDERS } from "~/screens/Scheduling/ConfirmedVisit/constants/calendar-providers";
import { addToCalendar } from "~/screens/Scheduling/ConfirmedVisit/utils/calendar";
import { normalizeVisitType } from "~/screens/Scheduling/ConfirmedVisit/utils/normalize-visit-type";
import {
  formatAppointmentTime,
  TIMEZONES,
} from "~/screens/Scheduling/InitialVisit/utils/date-time-utils";
import { ScheduleVisitResponse } from "~/services";
import { usePatientStore } from "~/stores";
import { icons } from "~/ui";
import { getVisitTypeTitle, tw } from "~/utils";

type ProviderCardProps = {
  appointment: ScheduleVisitResponse;
  className?: string;
  showAddToCalendar?: boolean;
};

export const ProviderCard = ({
  appointment: rawAppointment,
  className,
  showAddToCalendar,
}: ProviderCardProps) => {
  const { isMobile } = useBreakpoint();
  const appointmentDetails = usePatientStore(
    (state) => state.appointmentDetails,
  );

  const { appointment, provider } = rawAppointment;

  const formattedTime = formatAppointmentTime(
    appointment.startTime,
    appointment.duration,
    TIMEZONES.ET,
  );

  const providerInfo = (
    <>
      with {provider.name}
      {provider.title && `, ${provider.title}`}{" "}
    </>
  );

  const visitType = normalizeVisitType(appointment?.type);
  const eventTitle = visitType ? getVisitTypeTitle(visitType) : "Visit";

  const handleAddToCalendar = (
    calendarProvider: (typeof CALENDAR_PROVIDERS)[keyof typeof CALENDAR_PROVIDERS],
  ) => {
    addToCalendar(
      eventTitle,
      rawAppointment,
      calendarProvider,
      appointmentDetails.timezone,
    );
  };

  return (
    <section className={tw("bg-lightLavender/30 flex-col p-4", className)}>
      <div className="w-full items-center justify-center">
        <div className="mb-4 flex w-full justify-center">
          {provider?.imageUrl ? (
            <img
              src={provider.imageUrl}
              alt={provider.name}
              className={tw(
                "rounded-full object-cover",
                isMobile ? "size-16" : "size-20",
              )}
            />
          ) : (
            <icons.outline.UserIcon
              className={tw(
                "rounded-full bg-gray-100 p-2 text-gray-500",
                isMobile ? "size-16" : "size-20",
              )}
            />
          )}
        </div>
      </div>

      <h2 className="text-center font-medium text-gray-800">
        {eventTitle} {isMobile && <br />} {providerInfo}
      </h2>

      <div className="mt-4 flex flex-col items-center gap-2">
        <div className="flex items-center gap-1 text-neutral-950">
          <icons.outline.CalendarIcon className="size-5" />
          <p className="text-sm">{appointment.date}</p>
        </div>

        <div className="flex items-center gap-1 text-neutral-950">
          <icons.outline.ClockIcon className="size-5" />
          <p className="text-sm">
            {appointmentDetails.formattedTime || formattedTime}
          </p>
        </div>

        {showAddToCalendar && (
          <button
            className="text-violet mt-4 flex cursor-pointer items-center gap-1"
            onClick={() => handleAddToCalendar(CALENDAR_PROVIDERS.GOOGLE)}
          >
            <icons.outline.CalendarDaysIcon className="size-5" />
            <span>Add to Google Calendar</span>
          </button>
        )}

        {showAddToCalendar && (
          <button
            className="text-violet mb-2 flex cursor-pointer items-center gap-1"
            onClick={() => handleAddToCalendar(CALENDAR_PROVIDERS.OUTLOOK)}
          >
            <icons.outline.CalendarDaysIcon className="size-5" />
            <span>Add to Outlook Calendar</span>
          </button>
        )}
      </div>
    </section>
  );
};
